import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import {
  LogOut,
  PhoneCall,
  MessageSquareQuote,
  HeartPulse,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const ICON_OPTIONS = ["HeartPulse", "Activity", "Dumbbell", "Sparkles", "Stethoscope"];

function Shell({ children }) {
  const nav = useNavigate();
  const logout = () => {
    setToken(null);
    nav("/admin/login", { replace: true });
  };
  return (
    <div data-testid={ADMIN.dashboard} className="min-h-screen bg-cream text-ink flex">
      <aside className="hidden md:flex w-64 flex-col bg-surface border-r border-line p-6">
        <div>
          <div className="font-serif text-2xl text-ink">ReGain</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-ink-muted mt-1">
            Admin console
          </div>
        </div>
        <nav className="mt-10 space-y-1">
          {[
            { to: "/admin/callbacks", label: "Callbacks", icon: PhoneCall, id: ADMIN.navCallbacks },
            { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, id: ADMIN.navTestimonials },
            { to: "/admin/services", label: "Services", icon: HeartPulse, id: ADMIN.navServices },
          ].map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-sage text-cream" : "text-ink-muted hover:text-ink hover:bg-cream"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs text-ink-muted hover:text-ink px-3 py-2"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View site
          </a>
          <button
            data-testid={ADMIN.logoutBtn}
            onClick={logout}
            className="flex items-center gap-2 text-xs text-ink-muted hover:text-ink px-3 py-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 max-w-6xl">{children}</main>
    </div>
  );
}

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink tracking-tight">{title}</h1>
        {subtitle && <p className="text-ink-muted text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Renders one of three states: loading spinner, empty message, or the children (list). */
function ListState({ loading, empty, emptyMessage, children }) {
  if (loading) {
    return (
      <div className="p-10 text-center text-ink-muted text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }
  if (empty) {
    return <div className="p-12 text-center text-ink-muted">{emptyMessage}</div>;
  }
  return children;
}

// ---------------- Callbacks ----------------
function CallbacksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/callback-requests");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (item, status) => {
    try {
      await api.patch(`/admin/callback-requests/${item.id}`, { status });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/admin/callback-requests/${deletingId}`);
      toast.success("Deleted");
      setDeletingId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <PageHeader
        title="Callback Requests"
        subtitle="Patients who asked to be called back"
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total", value: items.length },
          { label: "New", value: items.filter((x) => x.status === "new").length },
          { label: "Contacted", value: items.filter((x) => x.status === "contacted").length },
          { label: "Closed", value: items.filter((x) => x.status === "closed").length },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl bg-surface border border-line p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-muted">{s.label}</div>
            <div className="font-serif text-3xl text-ink mt-1.5">{s.value}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-surface border border-line overflow-hidden">
        <ListState
          loading={loading}
          empty={items.length === 0}
          emptyMessage="No callback requests yet."
        >
          <div className="divide-y divide-line">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={ADMIN.callbackRow(it.id)}
                className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-ink">{it.name}</div>
                    <a
                      href={`tel:${it.phone}`}
                      className="text-sm text-sage hover:underline"
                    >
                      {it.phone}
                    </a>
                    {it.concern && (
                      <Badge variant="outline" className="border-line text-ink-muted">
                        {it.concern}
                      </Badge>
                    )}
                  </div>
                  {it.message && (
                    <p className="text-sm text-ink-muted mt-1.5 line-clamp-2">{it.message}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                    <span>{new Date(it.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span>WA/SMS: {it.notification_status}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={it.status}
                    onValueChange={(v) => setStatus(it, v)}
                  >
                    <SelectTrigger
                      data-testid={ADMIN.callbackStatusSelect(it.id)}
                      className="w-[140px] bg-cream border-line"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    data-testid={ADMIN.callbackDeleteBtn(it.id)}
                    onClick={() => setDeletingId(it.id)}
                    className="p-2 text-ink-muted hover:text-destructive rounded-md"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </ListState>
      </div>
      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent className="bg-cream border-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Delete callback?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------- Testimonials ----------------
function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const blank = { name: "", quote: "", location: "", age: "", image_url: "", active: true };

  const save = async (form) => {
    const payload = {
      name: form.name,
      quote: form.quote,
      location: form.location || null,
      age: form.age ? Number(form.age) : null,
      image_url: form.image_url || null,
      active: !!form.active,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/testimonials/${form.id}`, payload);
      } else {
        await api.post("/admin/testimonials", payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/admin/testimonials/${deletingId}`);
      toast.success("Deleted");
      setDeletingId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <PageHeader
        title="Testimonials"
        subtitle="Patient stories shown on the homepage"
        action={
          <button
            data-testid={ADMIN.testimonialAddBtn}
            onClick={() => setEditing({ ...blank })}
            className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add testimonial
          </button>
        }
      />
      <div className="rounded-2xl bg-surface border border-line overflow-hidden">
        <ListState
          loading={loading}
          empty={items.length === 0}
          emptyMessage="No testimonials yet."
        >
          <div className="divide-y divide-line">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={ADMIN.testimonialRow(it.id)}
                className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                {it.image_url ? (
                  <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-sage/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-ink">{it.name}</div>
                    {!it.active && (
                      <Badge variant="outline" className="border-line text-ink-muted">
                        Hidden
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-ink-muted mt-1 line-clamp-2 italic">"{it.quote}"</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={ADMIN.testimonialEditBtn(it.id)}
                    onClick={() => setEditing({ ...it, age: it.age || "" })}
                    className="p-2 text-ink-muted hover:text-ink rounded-md"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={ADMIN.testimonialDeleteBtn(it.id)}
                    onClick={() => setDeletingId(it.id)}
                    className="p-2 text-ink-muted hover:text-destructive rounded-md"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </ListState>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream border-line max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing?.id ? "Edit testimonial" : "Add testimonial"}
            </DialogTitle>
            <DialogDescription>
              Patient quote shown on the homepage. Use the toggle to hide without deleting.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <TestimonialForm
              key={editing.id || "new"}
              initial={editing}
              onSave={save}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent className="bg-cream border-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Delete testimonial?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TestimonialForm({ initial, onSave }) {
  const [f, setF] = useState(initial);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f);
      }}
      className="space-y-4 pt-2"
    >
      <Field label="Name" required>
        <Input
          data-testid={ADMIN.testimonialFormName}
          value={f.name}
          onChange={(e) => u("name", e.target.value)}
          required
        />
      </Field>
      <Field label="Quote" required>
        <Textarea
          data-testid={ADMIN.testimonialFormQuote}
          value={f.quote}
          onChange={(e) => u("quote", e.target.value)}
          rows={3}
          required
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Location">
          <Input
            data-testid={ADMIN.testimonialFormLocation}
            value={f.location || ""}
            onChange={(e) => u("location", e.target.value)}
          />
        </Field>
        <Field label="Age">
          <Input
            data-testid={ADMIN.testimonialFormAge}
            value={f.age || ""}
            onChange={(e) => u("age", e.target.value)}
            type="number"
          />
        </Field>
      </div>
      <Field label="Image URL">
        <Input
          data-testid={ADMIN.testimonialFormImage}
          value={f.image_url || ""}
          onChange={(e) => u("image_url", e.target.value)}
          placeholder="https://…"
        />
      </Field>
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3">
          <Switch
            data-testid={ADMIN.testimonialFormActive}
            checked={!!f.active}
            onCheckedChange={(v) => u("active", v)}
          />
          <span className="text-sm text-ink">Show on homepage</span>
        </div>
        <button
          type="submit"
          data-testid={ADMIN.testimonialFormSubmit}
          className="rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-2.5 text-sm font-medium"
        >
          Save
        </button>
      </div>
    </form>
  );
}

// ---------------- Services ----------------
function ServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/services");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const blank = { title: "", tagline: "", description: "", icon: "Sparkles", order: items.length + 1, active: true };

  const save = async (form) => {
    const payload = {
      title: form.title,
      tagline: form.tagline,
      description: form.description,
      icon: form.icon || "Sparkles",
      order: form.order ? Number(form.order) : 0,
      active: !!form.active,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/services/${form.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/admin/services/${deletingId}`);
      toast.success("Deleted");
      setDeletingId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <PageHeader
        title="Services"
        subtitle="Care pillars shown on the homepage"
        action={
          <button
            data-testid={ADMIN.serviceAddBtn}
            onClick={() => setEditing({ ...blank })}
            className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add service
          </button>
        }
      />
      <div className="rounded-2xl bg-surface border border-line overflow-hidden">
        <ListState
          loading={loading}
          empty={items.length === 0}
          emptyMessage="No services yet."
        >
          <div className="divide-y divide-line">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={ADMIN.serviceRow(it.id)}
                className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
              >
                <div className="w-10 text-center text-sm text-ink-muted">#{it.order}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-ink">{it.title}</div>
                    <Badge variant="outline" className="border-line text-ink-muted">
                      {it.icon}
                    </Badge>
                    {!it.active && (
                      <Badge variant="outline" className="border-line text-ink-muted">
                        Hidden
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-ink-muted mt-1 line-clamp-2">
                    {it.tagline} — {it.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={ADMIN.serviceEditBtn(it.id)}
                    onClick={() => setEditing({ ...it })}
                    className="p-2 text-ink-muted hover:text-ink rounded-md"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={ADMIN.serviceDeleteBtn(it.id)}
                    onClick={() => setDeletingId(it.id)}
                    className="p-2 text-ink-muted hover:text-destructive rounded-md"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </ListState>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream border-line max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing?.id ? "Edit service" : "Add service"}
            </DialogTitle>
            <DialogDescription>
              Care pillar shown on the homepage. Lower order numbers appear first.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <ServiceForm key={editing.id || "new"} initial={editing} onSave={save} />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent className="bg-cream border-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Delete service?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ServiceForm({ initial, onSave }) {
  const [f, setF] = useState(initial);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f);
      }}
      className="space-y-4 pt-2"
    >
      <Field label="Title" required>
        <Input
          data-testid={ADMIN.serviceFormTitle}
          value={f.title}
          onChange={(e) => u("title", e.target.value)}
          required
        />
      </Field>
      <Field label="Tagline" required>
        <Input
          data-testid={ADMIN.serviceFormTagline}
          value={f.tagline}
          onChange={(e) => u("tagline", e.target.value)}
          required
        />
      </Field>
      <Field label="Description" required>
        <Textarea
          data-testid={ADMIN.serviceFormDescription}
          value={f.description}
          onChange={(e) => u("description", e.target.value)}
          rows={3}
          required
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Icon">
          <Select value={f.icon} onValueChange={(v) => u("icon", v)}>
            <SelectTrigger data-testid={ADMIN.serviceFormIcon}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ICON_OPTIONS.map((i) => (
                <SelectItem key={i} value={i}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Order">
          <Input
            data-testid={ADMIN.serviceFormOrder}
            value={f.order}
            onChange={(e) => u("order", e.target.value)}
            type="number"
          />
        </Field>
      </div>
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3">
          <Switch
            data-testid={ADMIN.serviceFormActive}
            checked={!!f.active}
            onCheckedChange={(v) => u("active", v)}
          />
          <span className="text-sm text-ink">Show on homepage</span>
        </div>
        <button
          type="submit"
          data-testid={ADMIN.serviceFormSubmit}
          className="rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-2.5 text-sm font-medium"
        >
          Save
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] text-ink-muted">
        {label}
        {required && <span className="text-terracotta"> *</span>}
      </Label>
      {children}
    </div>
  );
}

// ---------------- Root ----------------
export default function AdminDashboard() {
  return (
    <Shell>
      <Routes>
        <Route index element={<Navigate to="/admin/callbacks" replace />} />
        <Route path="callbacks" element={<CallbacksPage />} />
        <Route path="testimonials" element={<TestimonialsPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="*" element={<Navigate to="/admin/callbacks" replace />} />
      </Routes>
    </Shell>
  );
}
